import { cloneDeep } from "lodash-es";
import Utils from "../../../../util";
import { ResultRow } from "../../../data-structures/results/result-row.model";
import { Earnings } from "./earnings.model";
import { Revenues } from "./revenues.model";

interface ISPLInterface {
 'RESULTS.DATA.ISPL.REVENUES'?: Revenues;
 'RESULTS.DATA.ISPL.EXPENSES'?: Expenses;
 'RESULTS.DATA.ISPL.EARNINGS'?: Earnings;
}
/** Income Statement / Profit and Loss Statement */
export class ISPL {
  'RESULTS.DATA.ISPL.REVENUES';
  'RESULTS.DATA.ISPL.EXPENSES';
  'RESULTS.DATA.ISPL.EARNINGS';
  get revenues() {
    return this['RESULTS.DATA.ISPL.REVENUES'];
  }
  set revenues(value: Revenues) {
    this['RESULTS.DATA.ISPL.REVENUES'] = value;
  }
  get expenses() {
    return this['RESULTS.DATA.ISPL.EXPENSES'];
  }
  set expenses(value: Expenses) {
    this['RESULTS.DATA.ISPL.EXPENSES'] = value;
  }
  get earnings() {
    return this['RESULTS.DATA.ISPL.EARNINGS'];
  }
  set earnings(value: Earnings) {
    this['RESULTS.DATA.ISPL.EARNINGS'] = value;
  }

  constructor(param: ISPLInterface = {}) {
    this['RESULTS.DATA.ISPL.REVENUES'] = param['RESULTS.DATA.ISPL.REVENUES']
      ? new Revenues(param['RESULTS.DATA.ISPL.REVENUES'])
      : new Revenues();

    this['RESULTS.DATA.ISPL.EXPENSES'] = param['RESULTS.DATA.ISPL.EXPENSES']
      ? new Expenses(param['RESULTS.DATA.ISPL.EXPENSES'])
      : new Expenses();

    this['RESULTS.DATA.ISPL.EARNINGS'] = param['RESULTS.DATA.ISPL.EARNINGS']
      ? new Earnings(param['RESULTS.DATA.ISPL.EARNINGS'])
      : new Earnings();
  }

  deepCopy() {
    return new ISPL(cloneDeep(this));
  }
}

interface ExpensesInterface {
 'RESULTS.DATA.ISPL.FE'?: Record<string, ResultRow>;
 'RESULTS.DATA.ISPL.DE'?: Record<string, ResultRow>;
 'RESULTS.DATA.ISPL.UCF'?: Record<string, ResultRow>;
 'RESULTS.DATA.ISPL.LABOUR'?: Record<string, ResultRow>;
 'RESULTS.DATA.ISPL.MAINTENANCE'?: Record<string, ResultRow>;
 'RESULTS.DATA.ISPL.PMM'?: Record<string, ResultRow>;
 'RESULTS.DATA.ISPL.CONSUMABLES'?: Record<string, ResultRow>;
 'RESULTS.DATA.INSURANCE'?: Record<string, ResultRow>;
 'RESULTS.DATA.ISPL.OE'?: Record<string, ResultRow>;
 'RESULTS.DATA.ISPL.DA'?: DeprecationAmortization;
 'RESULTS.DATA.INTEREST'?: Record<string, ResultRow>;
}
export class Expenses {
  'RESULTS.DATA.ISPL.FE'?;
  'RESULTS.DATA.ISPL.DE'?;
  'RESULTS.DATA.ISPL.UCF'?;
  'RESULTS.DATA.ISPL.LABOUR'?;
  'RESULTS.DATA.ISPL.MAINTENANCE'?;
  'RESULTS.DATA.ISPL.PMM'?;
  'RESULTS.DATA.ISPL.CONSUMABLES'?;
  'RESULTS.DATA.INSURANCE'?;
  'RESULTS.DATA.ISPL.OE'?;
  'RESULTS.DATA.ISPL.DA';
  'RESULTS.DATA.INTEREST'?;

  /** feedstock expenses */
  get fe() {
    return this['RESULTS.DATA.ISPL.FE'];
  }
  set fe(value: Record<string, ResultRow>) {
    this['RESULTS.DATA.ISPL.FE'] = value;
  }
  /** digestate expenses */
  get de() {
    return this['RESULTS.DATA.ISPL.DE'];
  }
  set de(value: Record<string, ResultRow>) {
    this['RESULTS.DATA.ISPL.DE'] = value;
  }
  /** utilities (consumed fuels) */
  get ucf() {
    return this['RESULTS.DATA.ISPL.UCF'];
  }
  set ucf(value: Record<string, ResultRow>) {
    this['RESULTS.DATA.ISPL.UCF'] = value;
  }
  get labour() {
    return this['RESULTS.DATA.ISPL.LABOUR'];
  }
  set labour(value: Record<string, ResultRow>) {
    this['RESULTS.DATA.ISPL.LABOUR'] = value;
  }
  get maintenance() {
    return this['RESULTS.DATA.ISPL.MAINTENANCE'];
  }
  set maintenance(value: Record<string, ResultRow>) {
    this['RESULTS.DATA.ISPL.MAINTENANCE'] = value;
  }
  /** planned major maintenance */
  get pmm() {
    return this['RESULTS.DATA.ISPL.PMM'];
  }
  set pmm(value: Record<string, ResultRow>) {
    this['RESULTS.DATA.ISPL.PMM'] = value;
  }
  get consumables() {
    return this['RESULTS.DATA.ISPL.CONSUMABLES'];
  }
  set consumables(value: Record<string, ResultRow>) {
    this['RESULTS.DATA.ISPL.CONSUMABLES'] = value;
  }
  get insurance() {
    return this['RESULTS.DATA.INSURANCE'];
  }
  set insurance(value: Record<string, ResultRow>) {
    this['RESULTS.DATA.INSURANCE'] = value;
  }
  /** other expenses */
  get oe() {
    return this['RESULTS.DATA.ISPL.OE'];
  }
  set oe(value: Record<string, ResultRow>) {
    this['RESULTS.DATA.ISPL.OE'] = value;
  }
  /** depreciation & amortization (D&A) */
  get da() {
    return this['RESULTS.DATA.ISPL.DA.RESULTS.DATA.ISPL.DA'];
  }
  set da(value: ResultRow) {
    this['RESULTS.DATA.ISPL.DA.RESULTS.DATA.ISPL.DA'] = value;
  }
  get interest() {
    return this['RESULTS.DATA.INTEREST'];
  }
  set interest(value: Record<string, ResultRow>) {
    this['RESULTS.DATA.INTEREST'] = value;
  }

  constructor(param: ExpensesInterface = {}) {
    makeExpense('RESULTS.DATA.ISPL.FE', param, this)
    makeExpense('RESULTS.DATA.ISPL.DE', param, this)
    makeExpense('RESULTS.DATA.ISPL.UCF', param, this)
    makeExpense('RESULTS.DATA.ISPL.LABOUR', param, this)
    makeExpense('RESULTS.DATA.ISPL.MAINTENANCE', param, this)
    makeExpense('RESULTS.DATA.ISPL.PMM', param, this)
    makeExpense('RESULTS.DATA.ISPL.CONSUMABLES', param, this)
    makeExpense('RESULTS.DATA.INSURANCE', param, this)
    makeExpense('RESULTS.DATA.ISPL.OE', param, this)
    makeExpense('RESULTS.DATA.INTEREST', param, this)

    this['RESULTS.DATA.ISPL.DA'] = param['RESULTS.DATA.ISPL.DA']
      ? new DeprecationAmortization(param['RESULTS.DATA.ISPL.DA'])
      : new DeprecationAmortization();
  }

  deepCopy() {
    return new Expenses(cloneDeep(this));
  }
}

function makeExpense(key: string, param, obj) {
  if (param[key]) {
    obj[key] = Utils.ObjectMapper(param[key] || {}, Utils.toResultRows);
  } else {
    delete obj[key];
  }
}


interface DeprecationAmortizationInterface {
 'RESULTS.DATA.ISPL.DA'?: ResultRow;
}
class DeprecationAmortization {
  'RESULTS.DATA.ISPL.DA';
  constructor(param: DeprecationAmortizationInterface = {}) {
    this['RESULTS.DATA.ISPL.DA'] = param['RESULTS.DATA.ISPL.DA']
      ? new ResultRow(param['RESULTS.DATA.ISPL.DA'])
      : new ResultRow();
  }

  deepCopy() {
    return new DeprecationAmortization(cloneDeep(this));
  }
}